import { Link, useRouterState } from '@tanstack/react-router';
import {
  MessageCircle,
  User,
  Coins,
  Banknote,
  Building2,
  FileText,
  Send,
  Bell,
  Dice5,
  Calendar,
  Ticket,
  BarChart3,
  Link2,
  Settings,
  LogOut,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

const SECTIONS: NavSection[] = [
  {
    items: [
      { to: '/chats', label: 'Chats', icon: MessageCircle },
      { to: '/usuarios', label: 'Usuarios', icon: User },
      { to: '/clientes', label: 'Clientes', icon: Coins },
    ],
  },
  {
    title: 'FINANZAS',
    items: [
      { to: '/retiros', label: 'Retiros', icon: Banknote },
      { to: '/cuentas', label: 'Cuentas', icon: Building2 },
      { to: '/auditoria', label: 'Auditoría', icon: FileText },
    ],
  },
  {
    title: 'MARKETING',
    items: [
      { to: '/mensajes', label: 'Mensajes', icon: Send },
      { to: '/notificaciones', label: 'Notificaciones', icon: Bell },
      { to: '/eventos', label: 'Eventos', icon: Dice5 },
      { to: '/bonos', label: 'Bonos', icon: Calendar },
      { to: '/cupones', label: 'Cupones', icon: Ticket },
    ],
  },
  {
    title: 'SISTEMA',
    items: [
      { to: '/metricas', label: 'Métricas', icon: BarChart3 },
      { to: '/apis', label: 'APIs', icon: Link2 },
      { to: '/ajustes', label: 'Ajustes', icon: Settings },
    ],
  },
];

export function Sidebar() {
  const { user, logout } = useAuth();
  const { location } = useRouterState();
  const initials = (user?.display_name || user?.username || '??').slice(0, 2).toUpperCase();

  return (
    <aside className="flex h-screen w-64 shrink-0 flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex h-16 items-center gap-2 border-b border-sidebar-border px-6">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-sidebar-accent text-base font-bold">
          CC
        </div>
        <div>
          <div className="text-sm font-semibold leading-tight">Clone Chat</div>
          <div className="text-xs text-sidebar-foreground/60">Admin Panel</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {SECTIONS.map((section, i) => (
          <div key={i} className={cn(i > 0 && 'mt-6')}>
            {section.title && (
              <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/50">
                {section.title}
              </div>
            )}
            <ul className="space-y-1">
              {section.items.map((item) => {
                const isActive = location.pathname.startsWith(item.to);
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                          : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-sidebar-border p-3">
        <div className="flex items-center gap-3 rounded-md px-2 py-2">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground text-xs">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{user?.display_name || user?.username || 'Invitado'}</div>
            <div className="truncate text-xs text-sidebar-foreground/60">{user?.role || '—'}</div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
            onClick={() => void logout()}
            title="Salir"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </aside>
  );
}
